import { Link } from 'wouter-preact';
import { ChevronRight } from 'lucide-preact';
import type { Analysis } from '@teacher-shared/analysis';
import { TeachingPage, ClassSwitcher, useClass, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, studentHref, Card, StatusChip, PerfChip, pct, Plain } from '@/components/teaching/ui';
import { KpiRow, KpiCard } from '@/components/teaching/kit';

export function Struggling() {
  return <WithData state={useAnalysis()} title="Who needs help">{({ a, A }) => <StrugglingView a={a} A={A} />}</WithData>;
}

function StrugglingView({ a, A }: { a: string; A: Analysis }) {
  const [cls, setCls] = useClass(A.classes);
  const list = A.struggling(cls);
  const all = A.summariesIn(cls);
  const awaiting = all.filter((s) => s.student.status !== 'completed');
  const partial = all.filter((s) => s.student.status === 'completed' && !s.eligible);
  const assessed = all.filter((s) => s.student.status === 'completed').length;

  return (
    <TeachingPage title="Who needs help" breadcrumb={breadcrumbOf(A)} banner={<ProvisionalBanner A={A} />} actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>}>
      <KpiRow>
        <KpiCard value={list.length} label="Need support" tone="#b42318" hint={`of ${assessed} assessed`} />
        <KpiCard value={assessed} label="Assessed" />
        <KpiCard value={awaiting.length} label="Awaiting evidence" hint="absent or catch-up, never ranked" />
        <KpiCard value={partial.length} label="Partial papers" hint="not ranked until complete" />
      </KpiRow>
      <div class="mb-4">
        <div class="text-[20px] font-semibold text-[var(--color-text)]">
          {list.length} of {assessed} students need support
        </div>
        <div class="text-[12.5px] text-[var(--color-text-muted)] mt-1">
          Worst first. Each reason comes from their own marked answers. Absent and catch-up students are never ranked.
        </div>
      </div>

      <div class="flex flex-col gap-2">
        {list.map((s, i) => (
          <Link
            key={s.student.id}
            href={studentHref(a, s.student.id, cls)}
            class="group flex items-start gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 hover:border-[var(--color-accent)] transition-colors"
          >
            <div class="w-6 shrink-0 text-[12px] text-[var(--color-text-faint)] tabular-nums pt-0.5">{i + 1}</div>
            <div class="flex-1 min-w-0">
              <div class="flex flex-wrap items-center gap-2">
                <span class="text-[14px] font-medium text-[var(--color-text)]">{s.student.name}</span>
                <span class="text-[11px] text-[var(--color-text-faint)]">{s.student.cls}</span>
                <PerfChip rate={s.validCount ? s.masteredCount / s.validCount : null}>
                  {s.masteredCount}/{s.validCount} mastered
                </PerfChip>
                <span class="text-[11px] text-[var(--color-text-faint)] tabular-nums">{pct(s.attainment)} of marks</span>
                {!s.complete && <StatusChip status="partial" />}
              </div>
              <ul class="mt-1.5 space-y-0.5">
                {s.reasons.map((r) => (
                  <li key={r} class="text-[12.5px] text-[var(--color-text-muted)] before:content-['•'] before:mr-1.5 before:text-[var(--color-text-faint)]"><Plain text={r} /></li>
                ))}
              </ul>
            </div>
            <ChevronRight size={16} class="shrink-0 mt-1 text-[var(--color-text-faint)] group-hover:text-[var(--color-accent)]" />
          </Link>
        ))}
        {list.length === 0 && (
          <div class="text-[13px] text-[var(--color-text-muted)]">Nobody needs extra support in this class on this test. Check Teaching groups for who is ready to extend.</div>
        )}
      </div>

      {(awaiting.length > 0 || partial.length > 0) && (
        <div class="grid md:grid-cols-2 gap-3 mt-6">
          {awaiting.length > 0 && (
            <Card title="Awaiting evidence" sub="No paper yet. Shown as a status, never a score.">
              <div class="flex flex-col gap-1.5">
                {awaiting.map((s) => (
                  <div key={s.student.id} class="flex items-center gap-2 text-[13px]">
                    <span class="text-[var(--color-text)]">{s.student.name}</span>
                    <span class="text-[11px] text-[var(--color-text-faint)]">{s.student.cls}</span>
                    <span class="ml-auto"><StatusChip status={s.student.status} /></span>
                  </div>
                ))}
              </div>
            </Card>
          )}
          {partial.length > 0 && (
            <Card title="Partial evidence" sub={`Fewer than ${A.minEvidence} of ${A.items.length} answers readable. Not ranked.`}>
              {partial.map((s) => (
                <div key={s.student.id} class="text-[13px] text-[var(--color-text)]">{s.student.name} <span class="text-[11px] text-[var(--color-text-faint)]">{s.validCount}/{A.items.length}</span></div>
              ))}
            </Card>
          )}
        </div>
      )}
    </TeachingPage>
  );
}
